/**
 * アンカーリンク・バリデーション時のスクロール先で、
 * グローバルヘッダー等を考慮した上部余白（px）。
 * サイドバーからのスクロールとバリデーション時のスクロールで共通利用する。
 */

/** PC 時のスクロール先の上部余白（GlobalHeaderPc top 16px + min-height 80px + 余白 64px） */
export const SCROLL_TOP_OFFSET_PC = 160;

/** SP 時のスクロール先の上部余白（GlobalHeaderSp 72px + 余白 48px） */
export const SCROLL_TOP_OFFSET_SP = 120;

/**
 * GlobalHeaderSp の上部バー（`.global-header__sp-top`）の高さ（px）。
 * SpBottomNav は `position: fixed; bottom: 0` のため、ビューポート上端からのオフセットには含めない。
 */
export const GLOBAL_HEADER_SP_TOP_HEIGHT_PX = 72;

/**
 * SP とみなすビューポート幅の上限（この値以下を SP）。グローバルヘッダー・サイドバー・フロート等と揃える。
 * useMobile の MD_BREAK_POINT はこの値 + 1（= 1081）で、innerWidth < 1081 ⇔ innerWidth <= 1080 の判定に使用。
 * SCSS の $md-break-point（1080px）はこの値と同期すること。
 */
export const VIEWPORT_WIDTH_SP_MAX = 1080;

/**
 * SP 未ログイン時、GuestHeaderAuthLinks のログイン／新規登録をアイコン列にする幅の上限（px）。
 * 採用向けリンクはこの幅以下ではヘッダーから出さない（ピルは 729px 以上のみ）。
 * この値を超えるとピル表示（`GuestHeaderAuthLinks.vue` の `$guest-auth-responsive-pill-min: 729px`）に切り替わる。
 * {@link GlobalHeaderSp} のゲスト用ハンバーガー表示判定にも使用する。
 */
export const GUEST_HEADER_SP_COMPACT_MAX_PX = 728;

/**
 * グローバルヘッダー PC 表示・求人検索 2 列レイアウトの下限幅（px）。
 * `hooks/useMobile.ts` の `MD_BREAK_POINT` と同値（`VIEWPORT_WIDTH_SP_MAX + 1` = 1081）。
 */
export const LAYOUT_HEADER_PC_MIN_WIDTH = VIEWPORT_WIDTH_SP_MAX + 1;

/**
 * 求人一覧のダイレクト／エージェント切替タブの `position: sticky` の `top`（PC ゲスト・参考用）。
 * レイアウトの `#app` `mt-5d5`（5.5rem）と揃えた文字列。`position: fixed` の pin 用 px は
 * {@link JOB_LIST_TAB_PIN_TOP_PX_PC_GUEST}（ヘッダー実寸）を使う。
 */
export const JOB_LIST_TAB_STICKY_TOP_REM_GUEST = '5.5rem';

/** `GlobalHeaderPc.vue` の `position: fixed` の `top`（px） */
export const GLOBAL_HEADER_PC_OFFSET_TOP_PX = 16;

/** `GlobalHeaderPc.vue` `.global-header__top` の `min-height`（px）。ゲスト時はナビ無しでこの帯のみ */
export const GLOBAL_HEADER_PC_TOP_BAR_MIN_HEIGHT_PX = 80;

/**
 * PC 未ログイン時、一覧切替タブ pin の `top`（px）。
 * `5.5rem`（root 16px なら 88px）ではなく、固定ヘッダーの実占有（16 + 80 = 96px）と一致させる。
 */
export const JOB_LIST_TAB_PIN_TOP_PX_PC_GUEST =
  GLOBAL_HEADER_PC_OFFSET_TOP_PX + GLOBAL_HEADER_PC_TOP_BAR_MIN_HEIGHT_PX;

/** 一覧が上に抜ける「完全フレームアウト」の何 px 手前で切替タブを隠し始めるか（IntersectionObserver の上方向オフセット相当） */
export const JOB_LIST_TAB_HIDE_BEFORE_LIST_FRAMEOUT_PX = 100;

/**
 * PC 幅（{@link VIEWPORT_WIDTH_SP_MAX} より大きい）かつログイン時。`mt-header-pc-10` 相当（レイアウト余白用）。
 * 一覧タブの `position: fixed` の `top` は {@link JOB_LIST_TAB_PIN_TOP_PX_PC_LOGGED_IN}（px 固定）。
 */
export const JOB_LIST_TAB_STICKY_TOP_REM_LOGGED_IN_WIDE = '10rem';

/** PC ログイン時、求人一覧ダイレクト／エージェント切替タブ pin の `top`（px）。`getJobListTabPinTopPx` 用 */
export const JOB_LIST_TAB_PIN_TOP_PX_PC_LOGGED_IN = 149;

/** タブスロットの下余白（rem）。`useJobListTabPin` のスペーサー高さに含める */
export const JOB_LIST_TAB_SLOT_MARGIN_BOTTOM_REM = 1;

/**
 * 一覧切替タブの固定 `top`（px）。`useJobListTabPin` 用。
 * SP: SP ヘッダー帯。PC ゲスト: {@link JOB_LIST_TAB_PIN_TOP_PX_PC_GUEST}。PC ログイン: {@link JOB_LIST_TAB_PIN_TOP_PX_PC_LOGGED_IN}。
 */
export function getJobListTabPinTopPx(params: {
  viewportWidth: number;
  userLoggedIn: boolean;
  rootFontSizePx: number;
}): number {
  if (params.viewportWidth <= VIEWPORT_WIDTH_SP_MAX) {
    return GLOBAL_HEADER_SP_TOP_HEIGHT_PX;
  }
  if (params.viewportWidth > VIEWPORT_WIDTH_SP_MAX && params.userLoggedIn) {
    return JOB_LIST_TAB_PIN_TOP_PX_PC_LOGGED_IN;
  }
  return JOB_LIST_TAB_PIN_TOP_PX_PC_GUEST;
}

/** 求人一覧の JobCard: この幅より大きいとき `variant="large"`、以下は `default`（通常） */
export const JOB_CARD_LARGE_VIEWPORT_MIN_WIDTH = 1048;

/**
 * 現在のビューポート幅に応じたスクロール上部余白を返す
 */
export function getScrollTopOffset(viewportWidth: number = window.innerWidth): number {
  return viewportWidth <= VIEWPORT_WIDTH_SP_MAX ? SCROLL_TOP_OFFSET_SP : SCROLL_TOP_OFFSET_PC;
}

/** 求人検索で検索ボタン押下直後、即時トップ化したあとスクロールを一瞬抑止する時間（ms） */
const JOB_SEARCH_SEARCH_START_SCROLL_LOCK_MS = 280;

/**
 * 検索実行の瞬間に呼ぶ。アニメーションなしでビューポートを最上部に固定し、
 * 直後だけ `overflow` / `touch-action` を抑止する（データ取得完了を待たない）。
 */
export function snapWindowToTopWithBriefScrollLock(): void {
  if (typeof window === 'undefined') return;
  const body = document.body;
  const html = document.documentElement;
  const prevBodyOverflow = body.style.overflow;
  const prevHtmlOverflow = html.style.overflow;
  const prevTouchAction = body.style.touchAction;
  body.style.overflow = 'hidden';
  html.style.overflow = 'hidden';
  body.style.touchAction = 'none';

  window.scrollTo(0, 0);
  html.scrollTop = 0;
  body.scrollTop = 0;

  window.setTimeout(() => {
    body.style.overflow = prevBodyOverflow;
    html.style.overflow = prevHtmlOverflow;
    body.style.touchAction = prevTouchAction;
  }, JOB_SEARCH_SEARCH_START_SCROLL_LOCK_MS);
}
